import { FC, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Icon, InfoTooltipModal, Modal } from 'src/components';
import { useShowCommunityMems } from 'src/hooks';
import { Mem } from 'src/api/learningSessions/types';
import { MemCard } from '../MemCard';
import { CreateMemButton, CreateMemOnCreated } from '../CreateMemButton';
import { isAlwaysShownMem, memSortPriority } from '../memVisibility';
import * as S from './styles';

const VISIBLE_MEMS_COUNT = 2;

const msgs = defineMessages({
  title: { id: 'mem_gallery_title', defaultMessage: 'Mems' },
  tooltipLine1: {
    id: 'mem_gallery_tooltip_line1',
    defaultMessage:
      'Studies show you are more likely to remember a word if you associate it with a story or a pun.',
  },
  tooltipLine2: {
    id: 'mem_gallery_tooltip_line2',
    defaultMessage:
      'For example, to remember that "Ananas" means "Pineapple", imagine someone named Anna with a pineapple for a nose (nas).',
  },
});

export interface MemGalleryProps {
  mems: Mem[];
  learnableId: number;
  currentUserId: number | null;
  itemValue?: string | null;
  definitionValue?: string | null;
  targetLanguageName?: string;
  sourceLanguageName?: string;
  onCreateClick?: () => void;
  onMemCreated: (newMem: Mem) => void;
  onMemDeleted: (memId: number) => void;
  onMemEdited: (updatedMem: Mem) => void;
  showCommunityMems?: boolean;
  /** Lighten title/actions for use on a dark video overlay panel. */
  overlay?: boolean;
}

export const MemGallery: FC<MemGalleryProps> = ({
  mems,
  learnableId,
  currentUserId,
  itemValue,
  definitionValue,
  targetLanguageName,
  sourceLanguageName,
  onCreateClick,
  onMemCreated,
  onMemDeleted,
  onMemEdited,
  showCommunityMems = false,
  overlay = false,
}) => {
  const intl = useIntl();
  const { showCommunityMems: showCommunityMemsShortcut } = useShowCommunityMems();
  const [aiPendingMemIds, setAiPendingMemIds] = useState<Set<number>>(new Set());
  const [isAllMemsOpen, setIsAllMemsOpen] = useState(false);
  const includeCommunityMems = showCommunityMems || showCommunityMemsShortcut;

  // Official `memrise` mems and the learner's own (own first). Community mems
  // stay hidden unless the settings toggle or ctrl+option+command+shift+c is on.
  const galleryMems = useMemo(() => {
    const visible = includeCommunityMems
      ? mems
      : mems.filter(mem => isAlwaysShownMem(mem, currentUserId));
    return [...visible].sort(
      (a, b) => memSortPriority(a, currentUserId) - memSortPriority(b, currentUserId),
    );
  }, [mems, currentUserId, includeCommunityMems]);

  const handleCreated: CreateMemOnCreated = (newMem, { aiPending }) => {
    onMemCreated(newMem);
    if (aiPending) {
      setAiPendingMemIds(prev => new Set([...prev, newMem.id]));
    }
  };

  const handleDeleted = (memId: number) => {
    onMemDeleted(memId);
  };

  const handleEdited = (updatedMem: Mem) => {
    onMemEdited(updatedMem);
  };

  const visibleMems = galleryMems.slice(0, VISIBLE_MEMS_COUNT);
  const remainingMems = galleryMems.slice(VISIBLE_MEMS_COUNT);

  const tooltip = (
    <>
      {intl.formatMessage(msgs.tooltipLine1)}
      <br />
      <br />
      {intl.formatMessage(msgs.tooltipLine2)}
    </>
  );

  const createButtonProps = {
    learnableId,
    onCreated: handleCreated,
    onCreateClick,
    itemValue,
    definitionValue,
    targetLanguageName,
    sourceLanguageName,
  };

  const renderMemCard = (mem: Mem, { isExpanded = false } = {}) => (
    <MemCard
      key={mem.id}
      mem={mem}
      currentUserId={currentUserId}
      onDeleted={handleDeleted}
      onEdited={handleEdited}
      initialAiPending={aiPendingMemIds.has(mem.id)}
      isExpanded={isExpanded}
    />
  );

  return (
    <S.GalleryWrapper data-testid="mem-gallery" $overlay={overlay}>
      <S.TitleRow>
        <S.GalleryTitle data-testid="mem-gallery-title">
          {intl.formatMessage(msgs.title)}
        </S.GalleryTitle>
        <S.TitleInfoIcon data-testid="mem-gallery-info">
          <InfoTooltipModal tooltip={tooltip} />
        </S.TitleInfoIcon>
        {remainingMems.length > 0 && (
          <S.ViewAllMemsButton
            type="button"
            onClick={() => setIsAllMemsOpen(true)}
            data-testid="mem-gallery-view-all"
          >
            <Icon name="eye" />
          </S.ViewAllMemsButton>
        )}
      </S.TitleRow>
      <S.ScrollContainer>
        {/* A learner can add as many mems as they like to a learnable. */}
        <CreateMemButton {...createButtonProps} />
        {visibleMems.map(mem => renderMemCard(mem))}
      </S.ScrollContainer>

      {isAllMemsOpen && (
        <Modal
          isOpen
          handleClose={() => setIsAllMemsOpen(false)}
          shouldCloseOnOutsideClick
          shouldCloseOnEsc
          darkCloseIcon
        >
          <S.AllMemsList data-testid="mem-gallery-all-modal">
            {remainingMems.map(mem => renderMemCard(mem, { isExpanded: true }))}
          </S.AllMemsList>
        </Modal>
      )}
    </S.GalleryWrapper>
  );
};
